(() => {
  const SELECTOR = '[data-aio-menu], #menu-mb__ul';

  class AllInOneMenu {
    constructor(root) {
      this.root = root;
      this.closeTimers = new WeakMap();
      this.pageSize = Number(root.dataset.aioPageSize || 8);
      this.lastPointerType = '';
      this.pointerDownNavItem = null;
      this.pointerDownNavWasOpen = false;
      this.bind();
      this.root.querySelectorAll('[data-aio-tabpanel]').forEach((panel) => this.setupPagination(panel));
    }

    bind() {
      this.root.addEventListener('click', (event) => this.onClick(event));
      this.root.addEventListener('keydown', (event) => this.onKeydown(event));
      this.root.addEventListener('pointerdown', (event) => {
        this.lastPointerType = event.pointerType || '';
        const navLink = event.target.closest('.aio-menu__nav-link');
        this.pointerDownNavItem = navLink?.closest('[data-aio-nav-item]') || null;
        this.pointerDownNavWasOpen = this.pointerDownNavItem?.classList.contains('is-open') || false;
      });

      if (this.root.matches('[data-aio-menu]')) {
        this.root.querySelectorAll('[data-aio-tab]').forEach((tab) => {
          tab.addEventListener('pointerenter', (event) => {
            if (event.pointerType === 'mouse' && !tab.classList.contains('is-active')) this.activateTab(tab);
          });
        });
        this.root.querySelectorAll('[data-aio-nav-item]').forEach((item) => {
          const panel = item.querySelector('[data-aio-panel]');
          item.addEventListener('pointerenter', (event) => {
            if (event.pointerType === 'mouse') this.openDesktop(item);
          });
          item.addEventListener('pointerleave', (event) => {
            if (event.pointerType === 'mouse') this.scheduleClose(item);
          });
          item.addEventListener('focusin', () => this.openDesktop(item));
          item.addEventListener('focusout', (event) => {
            if (!item.contains(event.relatedTarget)) this.scheduleClose(item);
          });
          panel?.addEventListener('pointerenter', (event) => {
            if (event.pointerType === 'mouse') this.openDesktop(item);
          });
          panel?.addEventListener('pointerleave', (event) => {
            if (event.pointerType === 'mouse') this.scheduleClose(item);
          });
        });
        document.addEventListener('pointerdown', (event) => {
          if (!this.root.contains(event.target)) {
            this.root.querySelectorAll('[data-aio-nav-item].is-open').forEach((item) => this.closeDesktop(item));
          }
        });
      }
    }

    onClick(event) {
      const navLink = event.target.closest('.aio-menu__nav-link');
      if (navLink && this.root.contains(navLink)) {
        const item = navLink.closest('[data-aio-nav-item]');
        const hasPanel = item?.querySelector('[data-aio-panel]');
        const touchActivation = ['touch', 'pen'].includes(event.pointerType || this.lastPointerType);
        const wasOpenAtPointerDown = this.pointerDownNavItem === item && this.pointerDownNavWasOpen;
        this.lastPointerType = '';
        this.pointerDownNavItem = null;
        this.pointerDownNavWasOpen = false;
        if (hasPanel && touchActivation && !wasOpenAtPointerDown) {
          event.preventDefault();
          this.openDesktop(item);
          return;
        }
      }

      const tab = event.target.closest('[data-aio-tab]');
      if (tab && this.root.contains(tab)) {
        event.preventDefault();
        this.activateTab(tab);
        return;
      }

      const open = event.target.closest('[data-aio-mobile-open]');
      if (open) {
        const item = open.closest('[data-aio-mobile-menu]');
        const view = item?.querySelector('[data-aio-mobile-view]');
        if (!item || !view) return;
        const initialTab = [...view.querySelectorAll('[data-aio-tab]')].find((tab) => tab.dataset.aioTab === open.dataset.aioInitialTab);
        if (initialTab) this.activateTab(initialTab);
        item._aioLastTrigger = open;
        this.root.classList.add('is-aio-detail');
        item.classList.add('is-active');
        item.querySelectorAll('[data-aio-mobile-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', String(trigger === open)));
        view.hidden = false;
        view.querySelector('[data-aio-mobile-back]')?.focus();
        return;
      }

      const back = event.target.closest('[data-aio-mobile-back]');
      if (back) this.closeMobile(back.closest('[data-aio-mobile-menu]'));

      const previous = event.target.closest('[data-aio-prev]');
      const next = event.target.closest('[data-aio-next]');
      if (previous || next) {
        event.preventDefault();
        const panel = event.target.closest('[data-aio-tabpanel]');
        const navItem = panel?.closest('[data-aio-nav-item]');
        if (navItem) clearTimeout(this.closeTimers.get(navItem));
        this.showPage(panel, Number(panel.dataset.aioPage || 1) + (next ? 1 : -1));
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        const mobileItem = event.target.closest('[data-aio-mobile-menu].is-active');
        if (mobileItem) {
          this.closeMobile(mobileItem);
          return;
        }
        const item = event.target.closest('[data-aio-nav-item]');
        if (item) {
          this.closeDesktop(item);
          item.querySelector('.aio-menu__nav-link')?.focus();
        }
      }

      const tab = event.target.closest('[data-aio-tab]');
      if (!tab || !['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(event.key)) return;
      const tabs = [...tab.parentElement.querySelectorAll('[data-aio-tab]')];
      const direction = ['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1;
      const next = tabs[(tabs.indexOf(tab) + direction + tabs.length) % tabs.length];
      event.preventDefault();
      next.focus();
      this.activateTab(next);
    }

    openDesktop(item) {
      const panel = item.querySelector('[data-aio-panel]');
      if (!panel) return;
      clearTimeout(this.closeTimers.get(item));
      this.root.querySelectorAll('[data-aio-nav-item].is-open').forEach((openItem) => {
        if (openItem !== item) this.closeDesktop(openItem);
      });
      item.classList.add('is-open');
      item.querySelector('.aio-menu__nav-link')?.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      const availableHeight = Math.max(240, window.innerHeight - panel.getBoundingClientRect().top - 12);
      panel.style.setProperty('--aio-available-height', `${availableHeight}px`);
    }

    scheduleClose(item) {
      clearTimeout(this.closeTimers.get(item));
      const configuredDelay = parseInt(getComputedStyle(this.root).getPropertyValue('--aio-close-delay'), 10);
      const delay = Number.isNaN(configuredDelay) ? 180 : configuredDelay;
      this.closeTimers.set(item, setTimeout(() => {
        const panel = item.querySelector('[data-aio-panel]');
        if (item.matches(':hover') || panel?.matches(':hover') || item.contains(document.activeElement)) return;
        this.closeDesktop(item);
      }, delay));
    }

    closeDesktop(item) {
      clearTimeout(this.closeTimers.get(item));
      item.classList.remove('is-open');
      item.querySelector('.aio-menu__nav-link')?.setAttribute('aria-expanded', 'false');
      const panel = item.querySelector('[data-aio-panel]');
      if (panel) {
        const defaultTab = panel.querySelector('[data-aio-default-tab]');
        if (defaultTab) this.activateTab(defaultTab);
        panel.hidden = true;
      }
    }

    closeMobile(item, restoreFocus = true) {
      if (!item) return;
      const triggers = item.querySelectorAll('[data-aio-mobile-open]');
      const view = item.querySelector('[data-aio-mobile-view]');
      item.classList.remove('is-active');
      this.root.classList.remove('is-aio-detail');
      triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      if (view) {
        const defaultTab = view.querySelector('[data-aio-default-tab]');
        if (defaultTab) this.activateTab(defaultTab);
        view.hidden = true;
      }
      if (restoreFocus) item._aioLastTrigger?.focus();
    }

    resetMobile() {
      this.root.querySelectorAll('[data-aio-mobile-menu].is-active').forEach((item) => this.closeMobile(item, false));
      this.root.classList.remove('is-aio-detail');
    }

    activateTab(tab) {
      const scope = tab.closest('[data-aio-panel], [data-aio-mobile-view]');
      if (!scope) return;
      const key = tab.dataset.aioTab;
      this.loadMobilePanel(scope, key);
      scope.querySelectorAll('[data-aio-tab]').forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      scope.querySelectorAll('[data-aio-tabpanel]').forEach((panel) => {
        const active = panel.dataset.aioTabpanel === key;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
        if (active) this.showPage(panel, 1);
      });
    }

    loadMobilePanel(scope, key) {
      if (!scope.matches('[data-aio-mobile-view]')) return;
      if (scope.querySelector(`[data-aio-tabpanel="${key}"]`)) return;
      const template = scope.querySelector(`[data-aio-mobile-panel-template="${key}"]`);
      const content = scope.querySelector('.aio-mobile-menu__content');
      if (!template || !content) return;
      content.append(template.content.cloneNode(true));
      template.remove();
    }

    setupPagination(panel) {
      if (!this.root.matches('[data-aio-menu]')) return;
      this.showPage(panel, 1);
    }

    showPage(panel, requestedPage) {
      if (!panel || !this.root.matches('[data-aio-menu]')) return;
      const cards = [...panel.querySelectorAll('[data-aio-card]')];
      const totalPages = Math.max(1, Math.ceil(cards.length / this.pageSize));
      const page = Math.min(totalPages, Math.max(1, requestedPage));
      panel.dataset.aioPage = String(page);
      cards.forEach((card, index) => {
        card.hidden = index < (page - 1) * this.pageSize || index >= page * this.pageSize;
      });
      const pagination = panel.querySelector('[data-aio-pagination]');
      if (!pagination) return;
      pagination.hidden = totalPages <= 1;
      pagination.querySelector('[data-aio-prev]').disabled = page === 1;
      pagination.querySelector('[data-aio-next]').disabled = page === totalPages;
      pagination.querySelector('[data-aio-page-status]').textContent = `${page} / ${totalPages}`;
    }
  }

  const initialize = (scope = document) => {
    const roots = [];
    if (scope.matches?.(SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll(SELECTOR));
    roots.forEach((root) => {
      if (root.dataset.aioInitialized === 'true' || !root.querySelector('[data-aio-nav-item], [data-aio-mobile-menu]')) return;
      root.dataset.aioInitialized = 'true';
      const instance = new AllInOneMenu(root);
      if (root.id === 'menu-mb__ul') {
        const drawer = root.closest('#t4s-menu-drawer');
        if (drawer && drawer.dataset.aioObserved !== 'true') {
          drawer.dataset.aioObserved = 'true';
          new MutationObserver(() => {
            if (drawer.getAttribute('aria-hidden') === 'true') instance.resetMobile();
          }).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
        }
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        // Translation apps add a large number of unrelated nodes. Avoid walking each
        // one unless the added subtree can actually contain an AIO menu instance.
        if (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR)) initialize(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
